import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { DateTime } from 'luxon';
import ShadowWrapper from '../ShadowWrapper/ShadowWrapper.jsx';
import styles from './HeaderNotification.styles.css';

const HeaderNotification = ({ data = {}, user, isAuthenticated }) => {
  const [alerts, setAlerts] = useState([]);
  const [opened, setOpened] = useState(true);
  const storageKey = 'crds-header-notification-closed';

  const getCurrentURLSlug = () => {
    const currentUrl = window.location.href;
    const urlWithoutDomain = currentUrl.split(window.location.origin)[1];
    return removeTrailingSlash(urlWithoutDomain);
  };

  const removeTrailingSlash = (url) => {
    if (!url) return '';
    if (url.startsWith('/')) {
      url = url.slice(1);
    }
    if (url.endsWith('/')) {
      url = url.slice(0, -1);
    }
    return url;
  };

  const removeQueryParams = (url) => {
    const queryStringIndex = url.indexOf('?');
    if (queryStringIndex !== -1) {
      return url.substring(0, queryStringIndex);
    }
    return url;
  };

  const isDateInRange = (dateStart, dateEnd) => {
    try {
      const nowIso = DateTime.local();

      if (!dateStart) {
        return false;
      }

      const startDateTime = DateTime.fromISO(dateStart);
      if (!startDateTime.isValid) {
        return false;
      }

      if (dateEnd) {
        const endDateTime = DateTime.fromISO(dateEnd);
        if (!endDateTime.isValid) {
          return false;
        }
        return startDateTime < nowIso && endDateTime > nowIso;
      }
      return startDateTime < nowIso;
    } catch (error) {
      return false;
    }
  };

  const isAudienceAllowed = (restriction) => {
    if (restriction === 'Everyone') return true;
    if (restriction === 'Logged-out Users' && !isAuthenticated) return true;
    if (restriction === 'Logged-in Users' && isAuthenticated) return true;
    return false;
  };

  const doesAlertApplyToCurrentPage = (currentPath, targetAudiences, alert) => {
    const alertPath = removeTrailingSlash(alert.single_page_alert);
    const hasSinglePageAlert = alert.single_page_alert && alert.single_page_alert.trim() !== '';

    // 1. If a single_page_alert is defined, restrict the alert to just that page (exact match or wildcard)
    if (hasSinglePageAlert) {
      if (alertPath.endsWith('*')) {
        const basePath = alertPath.slice(0, -1).toLowerCase();
        return currentPath.toLowerCase().startsWith(basePath);
      } else {
        return currentPath.toLowerCase() === alertPath.toLowerCase();
      }
    }

    // 2. Convert target audiences to path-friendly slugs (e.g. "East Side" → "eastside")
    const locationPaths = targetAudiences
      .map((audience) => {
        if (audience === 'Churchwide') return null;
        return audience.toLowerCase().replace(/\s+/g, '');
      })
      .filter(Boolean); // Remove nulls

    const isChurchwide = targetAudiences.includes('Churchwide');

    // 3. Location-based audience targeting:
    //     - Authenticated users see alerts for their campus sitewide
    //     - Unauthenticated users only see them if they're on the matching location page
    if (locationPaths.length > 0) {
      if (isAuthenticated) {
        return true;
      } else {
        return locationPaths.includes(currentPath.toLowerCase()) || isChurchwide;
      }
    }

    // 4. If no targeting info is defined, show the alert sitewide by default
    return true;
  };

  const isAlertVisible = (alert, currentPath, userSiteLocation) => {
    // Extract alert properties with defaults
    const restriction = alert.audienceRestriction || 'Everyone'; // Auth-based visibility rule
    const targetAudiences = alert.target_audience || []; // Campus/location targeting

    // 1. Check if the alert is active based on its publish window
    const hasValidDateRange = isDateInRange(alert.publish_at, alert.unpublish_at);

    // 2. Check if the user matches the alert’s required auth status
    const userIsValidAudience = isAudienceAllowed(restriction);

    // 3. Check if the user's site/campus is in the target list (only for authenticated users)
    const userMatchesTargetAudience = isAuthenticated
      ? targetAudiences.includes('Churchwide') || targetAudiences.includes(userSiteLocation)
      : true; // Unauthenticated users pass this check for now; page visibility is restricted elsewhere

    // 4. Check if the alert should show on the current page based on path and targeting
    const matchesPage = doesAlertApplyToCurrentPage(currentPath, targetAudiences, alert);

    const shouldDisplay =
      hasValidDateRange && userIsValidAudience && userMatchesTargetAudience && matchesPage;

    return shouldDisplay;
  };

  const filterAlerts = () => {
    if (!data.notifications) {
      return [];
    }
    const currentUrlSlug = getCurrentURLSlug();
    const slugWithoutParams = removeQueryParams(currentUrlSlug);
    let userSiteLocation =
      user?.campus?.Name !== 'Anywhere/Online' ? user?.campus?.Name : 'Churchwide';

    return data.notifications.filter((alert) =>
      isAlertVisible(alert, slugWithoutParams, userSiteLocation)
    );
  };

  useEffect(() => {
    setAlerts(filterAlerts());
    const isClosed = localStorage.getItem(storageKey);
    setOpened(!isClosed);
  }, []);

  const handleToggle = () => {
    const newOpened = !opened;
    setOpened(newOpened);
    if (!newOpened) {
      localStorage.setItem(storageKey, JSON.stringify(alerts));
    } else {
      localStorage.removeItem(storageKey);
    }
  };

  const renderAlerts = () => {
    return (
      <div class="expanded-alerts">
        {alerts.map((alert, index) => (
          <div
            class="alert-text center-text"
            key={index}
            dangerouslySetInnerHTML={{ __html: alert.description || alert.message }}
          ></div>
        ))}
      </div>
    );
  };

  const renderAlertsDropdown = () => {
    return (
      <Fragment>
        <div class="top-container" onClick={handleToggle}>
          {alerts.length !== 0 && (
            <button class="alerts">
              <crds-icon class="bell" name="bell-fill" size="16"></crds-icon>
              {alerts.length} alert{alerts.length === 1 ? '' : 's'}
              <span>
                <crds-icon
                  class="expand"
                  name={`chevron-${opened ? 'down' : 'up'}-thin`}
                  size="12"
                ></crds-icon>
              </span>
            </button>
          )}
        </div>
        {alerts.length !== 0 && opened && (
          <div class={`expanded-alerts ${!opened ? 'alerts-hidden' : ''}`}>{renderAlerts()}</div>
        )}
      </Fragment>
    );
  };

  const renderAlertsBanner = () => {
    return <Fragment>{renderAlerts()}</Fragment>;
  };

  if (!alerts) return null;

  return (
    <ShadowWrapper styles={styles}>
      {alerts.length === 1 ? renderAlertsBanner() : renderAlertsDropdown()}
    </ShadowWrapper>
  );
};

export default HeaderNotification;
